// #region Imports
import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetAllIndividualBookmarksQuery } from "marketplace/bookmarks/_api/bookmark-api";
import BookmarkedIndividual from "marketplace/bookmarks/individual-bookmarks/bookmarked-individual";
import {
  getIndividualsByIds,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { useGetIndividualsByIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import {
  createMRTColumnHelper,
  type MRT_TableOptions
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
// #endregion

// #region Styled Components
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
// #endregion

// #region Component Input Props
export interface BookmarkedIndividualsProps {
  selectedMemberUserIds?: Guid[];
  onSelected: (individual: Individual) => void;
};

const columnHelper = createMRTColumnHelper<BookmarkedIndividual>();

export function BookmarkedIndividuals(props: Readonly<BookmarkedIndividualsProps>) {
  const {
    selectedMemberUserIds,
    onSelected
  } = props;

  // #region Hooks
  const {
    data: bookmarksJson,
    isError: bookmarksError,
    isFetching: bookmarksFetching,
    isLoading: bookmarksLoading,
    refetch: refetchBookmarks,
  } = useGetAllIndividualBookmarksQuery();

  const bookmarks = useMemo(
    () => bookmarksJson?.map((b) => BookmarkedIndividual.fromJSON(b)).filter((b): b is BookmarkedIndividual => !!b) ?? [],
    [bookmarksJson],
  );

  // All individualIds to fetch
  const bookmarkedIndividualIds = useMemo(() => {
    return bookmarks?.map(b => b.individualId).filter((id): id is Guid => !!id) ?? []
  }, [bookmarks]);

  // Individual Selectors
  const individuals = useSelector(getIndividualsByIds(bookmarkedIndividualIds));
  const { isLoading: individualsLoading, isError: individualsError } = useGetIndividualsByIdsQuery(
    { ids: bookmarkedIndividualIds.map(id => id.value) },
    { skip: !bookmarkedIndividualIds.length }
  );

  const individualsById = useMemo(() => {
    const map: Record<string, Individual> = {};
    for (const ind of individuals) {
      map[ind.id.value] = ind;
    }
    return map;
  }, [individuals]);

  // #region Lifecycle Hooks
  useEffect(() => {
    if (bookmarksError) {
      enqueueSnackbar("Error loading bookmarks. Please try again.", { variant: 'error' });
    }
  }, [bookmarksError]);

  // #region Columns
  const renderNameColumn = useCallback((bookmark: BookmarkedIndividual) => {
    const id = bookmark.individualId;
    const individual = individualsById[id.value];
    const loadingText = individualsLoading ? "Loading..." : "Unknown Individual";

    const name =
      individual?.fullName ??
      (individualsError ? "Error loading" : loadingText);

    return (
      <NameCellContainer>
        <IndividualAvatar
          individual={individual}
        />
        <RowTitle>{name}</RowTitle>
      </NameCellContainer>
    );
  }, [individualsById, individualsLoading, individualsError]);

  const renderCategoriesColumn = useCallback((bookmark: BookmarkedIndividual) => {
    const id = bookmark.individualId;
    const individual = individualsById[id.value];
    const loadingText = individualsLoading ? "Loading..." : "None";
    const label = individualsError ? "Error loading" : loadingText;

    if (!individual?.profile?.categories) {
      return <Chip disabled label={label} />;
    }

    return individual.profile.categories.map((category) => (
      <Chip key={category} label={category} />
    ));
  }, [individualsById, individualsLoading, individualsError]);

  const renderDescriptionColumn = useCallback((bookmark: BookmarkedIndividual) => {
    const id = bookmark.individualId;
    const individual = individualsById[id.value];
    const loadingText = individualsLoading ? "Loading..." : "Description unavailable";
    const text = individualsError ? "Error loading" : loadingText;


    return individual?.profile.credentials ?? text;
  }, [individualsById, individualsLoading, individualsError]);

  const columns = useMemo(() => [
    columnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name",
    }),
    columnHelper.accessor(renderCategoriesColumn, {
      header: "Categories",
      id: "categories",
    }),
    columnHelper.accessor(renderDescriptionColumn, {
      header: "Description",
      id: "description",
    }),
  ], [renderNameColumn, renderCategoriesColumn, renderDescriptionColumn]);
  // #endregion

  // #region Table
  const tableOptions: MRT_TableOptions<BookmarkedIndividual> = {
    columns,
    data: bookmarks ?? [],
    // A picker: the row is a choice being made, not a record to act on. Removing a bookmark is the bookmarks page's job.
    enableRowActions: false,
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        const individual = individualsById[row.original.individualId.value];
        if (!individual) throw new Error("Individual not found for bookmark");
        onSelected(individual);
      },
      sx: {
        pointerEvents: selectedMemberUserIds?.some(id =>
          row.original?.individualId && id.isEqualTo(row.original.individualId)
        ) ? "none" : "auto",
        cursor: "pointer"
      },
    }),
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    enableColumnPinning: true,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiToolbarAlertBannerProps: bookmarksError
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    // onColumnFiltersChange: setColumnFilters,
    // onGlobalFilterChange: setGlobalFilter,
    // onPaginationChange: setPagination,
    // onSortingChange: setSorting,
    rowCount: bookmarks?.length ?? 0,
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: bookmarksError || individualsError,
      isLoading: bookmarksFetching,
      onRetry: () => void refetchBookmarks(),
      subject: "bookmarked individuals",
    }),
    state: {
      // columnFilters,
      // globalFilter,
      isLoading: bookmarksLoading,
      // pagination,
      showAlertBanner: bookmarksError || individualsError,
      showProgressBars: bookmarksLoading,
      // sorting,
    },
  }

  return <MRTNoMemo<BookmarkedIndividual> options={tableOptions} />;
}
